import React, { useCallback } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@ais-ui/ui-core';
import { PiPlusCircle } from 'react-icons/pi';
import { useMultiShapeDrawing, DrawingStyles } from '@ais-ui/react-openlayers';

const ADD_NEW_VALUE = '__add_new__';

/**
 * Shape selector dropdown for the drawing toolbar.
 * Renders nothing when MultiShapeDrawingContext is not active.
 * Adds a port-admin-specific "+ New Inner Area" option.
 */
export const PortAdminShapeSelector = () => {
  const ctx = useMultiShapeDrawing();

  const handleAddNewInnerArea = useCallback(() => {
    if (!ctx) return;
    const newId = `inner-new-${Date.now()}`;
    ctx.addShape({
      id: newId,
      label: 'New Inner Area',
      color: 'orange',
      transparency: 0.6,
      drawingStyle: DrawingStyles.InnerArea,
      geometry: null,
    });
  }, [ctx]);

  if (!ctx?.isActive || !ctx.activeShapeId) return null;

  const { shapes, activeShapeId, switchShape } = ctx;

  const handleChange = (value: string) => {
    if (value === ADD_NEW_VALUE) {
      handleAddNewInnerArea();
    } else {
      switchShape(value);
    }
  };

  return (
    <div className="flex items-center gap-2 pr-2 mr-2 border-r border-border">
      <Select value={activeShapeId} onValueChange={handleChange}>
        <SelectTrigger className="h-9 w-[180px] bg-muted/50 border-border text-sm">
          <SelectValue placeholder="Select shape" />
        </SelectTrigger>
        <SelectContent>
          {shapes.map((shape) => (
            <SelectItem key={shape.id} value={shape.id}>
              <span className="flex items-center gap-2">
                <span
                  className="size-2.5 rounded-full shrink-0"
                  style={{
                    backgroundColor:
                      shape.color === 'blue' ? '#3b82f6' : '#f97316',
                  }}
                />
                {shape.label}
              </span>
            </SelectItem>
          ))}
          <Separator className="my-1" />
          <SelectItem value={ADD_NEW_VALUE}>
            <span className="flex items-center gap-2 text-muted-foreground">
              <PiPlusCircle className="size-3.5" />
              New Inner Area
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
